import { Trash2 } from "lucide-react";
import type { EmployeeImportColumn, EmployeeImportRow, EmployeeRowErrors } from "@graftus/schema";
import { cn } from "@/lib/utils";
import { focusRing } from "./buttons";

export interface EditableColumn {
  key: EmployeeImportColumn;
  label: string;
  /** Placeholder when the cell is empty. */
  placeholder?: string;
  inputMode?: "text" | "email" | "tel" | "numeric";
}

interface EditableTableProps {
  columns: EditableColumn[];
  rows: EmployeeImportRow[];
  /** Validation per row id; cells with an error get the danger state. */
  errors: Record<string, EmployeeRowErrors>;
  errorLabel: (code: NonNullable<EmployeeRowErrors[keyof EmployeeRowErrors]>) => string;
  onChange: (rowId: string, column: EmployeeImportColumn, value: string) => void;
  onRemove: (rowId: string) => void;
  removeLabel: string;
  /** Column -> datalist id for suggestions (the team column). */
  datalists?: Partial<Record<EmployeeImportColumn, string>>;
}

/**
 * The employees table with a borderless input in every cell, so a bad row can
 * be fixed in place ("Rätta direkt i tabellen"). Rows with an error get a
 * warning wash, the cell itself the danger state and its message on focus.
 */
export function EditableTable({
  columns,
  rows,
  errors,
  errorLabel,
  onChange,
  onRemove,
  removeLabel,
  datalists = {},
}: EditableTableProps) {
  return (
    <div className="overflow-x-auto rounded-g-lg border border-g-line">
      <table className="w-full min-w-[720px] border-collapse text-[13.5px]">
        <thead>
          <tr className="bg-g-bg-input text-left">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="px-3 py-2.5 text-[11.5px] font-bold uppercase tracking-wide text-g-ink-500"
              >
                {column.label}
              </th>
            ))}
            <th scope="col" className="w-10 px-2 py-2.5">
              <span className="sr-only">{removeLabel}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowErrors = errors[row.id] ?? {};
            const bad = Object.keys(rowErrors).length > 0;
            return (
              <tr
                key={row.id}
                data-testid={`onboarding-row-${row.id}`}
                data-invalid={bad || undefined}
                className={cn("border-t border-g-line-row", bad ? "bg-g-warning-tint" : "bg-g-bg-card")}
              >
                {columns.map((column) => {
                  const code = rowErrors[column.key as keyof EmployeeRowErrors];
                  const value = (row[column.key] ?? "") as string;
                  const id = `cell-${row.id}-${column.key}`;
                  return (
                    <td key={column.key} className="p-1 align-top">
                      <input
                        id={id}
                        aria-label={`${column.label} ${row.name || row.email || row.id}`}
                        aria-invalid={code ? true : undefined}
                        aria-describedby={code ? `${id}-error` : undefined}
                        inputMode={column.inputMode}
                        list={datalists[column.key]}
                        value={value}
                        placeholder={column.placeholder}
                        onChange={(e) => onChange(row.id, column.key, e.currentTarget.value)}
                        className={cn(
                          "peer h-9 w-full rounded-g-xs border bg-transparent px-2 text-g-ink-900 transition-colors placeholder:text-g-ink-300",
                          code
                            ? "border-g-danger bg-g-danger-tint"
                            : "border-transparent hover:border-g-line-strong hover:bg-g-bg-card",
                          focusRing,
                        )}
                      />
                      {code ? (
                        <span
                          id={`${id}-error`}
                          className="mt-0.5 hidden px-2 text-[11.5px] font-medium text-g-danger peer-focus:block"
                        >
                          {errorLabel(code)}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
                <td className="p-1 align-top">
                  <button
                    type="button"
                    onClick={() => onRemove(row.id)}
                    aria-label={`${removeLabel}: ${row.name || row.email || row.id}`}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-g-xs text-g-ink-400 transition-colors hover:bg-g-danger-tint hover:text-g-danger",
                      focusRing,
                    )}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
